import { Link } from 'react-router-dom';
import { 
  MessageSquare, FileSearch, Palette, Code, Rocket, HeartHandshake,
  ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const steps = [
  {
    number: '01',
    title: 'Premier contact',
    description: 'On échange sur votre projet, vos besoins et vos objectifs. Je vous pose les bonnes questions pour bien comprendre votre activité.',
    icon: MessageSquare,
    details: ['Appel découverte gratuit', 'Analyse de vos besoins', 'Conseil personnalisé'],
    duration: '30 min'
  },
  {
    number: '02',
    title: 'Devis détaillé',
    description: 'Je vous envoie un devis clair et transparent, sans surprise. Vous savez exactement ce que vous payez.',
    icon: FileSearch,
    details: ['Prix fixe garanti', 'Détail des fonctionnalités', 'Délais estimés'],
    duration: '24-48h'
  },
  {
    number: '03',
    title: 'Maquette & Design',
    description: 'Je crée la maquette de votre site. Vous validez le design avant que je commence à coder.',
    icon: Palette,
    details: ['Design sur-mesure', 'Responsive mobile', 'Révisions incluses'],
    duration: '1-2 semaines'
  },
  {
    number: '04',
    title: 'Développement',
    description: 'Je développe votre site avec les dernières technologies. Code propre, performant et sécurisé.',
    icon: Code,
    details: ['Technologies modernes', 'Optimisation SEO', 'Site rapide'],
    duration: '2-4 semaines'
  },
  {
    number: '05',
    title: 'Mise en ligne',
    description: 'Votre site est mis en ligne ! Je m\'occupe de tout : hébergement, nom de domaine, certificat SSL.',
    icon: Rocket,
    details: ['Hébergement inclus', 'Formation utilisation', 'Support technique'],
    duration: '1-2 jours'
  },
  {
    number: '06',
    title: 'Suivi & Support',
    description: 'Je reste disponible après la mise en ligne. Maintenance, mises à jour, évolutions : je suis là.',
    icon: HeartHandshake,
    details: ['Support réactif', 'Maintenance continue', 'Évolutions possibles'],
    duration: 'Continu'
  }
];

export default function ProcessusPage() {
  return (
    <main data-testid="processus-page" className="pt-32">
      <SEO 
        title="Processus de création"
        description="Découvrez comment je crée votre site web étape par étape : premier contact, devis gratuit, maquettes, développement, mise en ligne. Un processus simple et transparent."
        path="/processus"
      />
      
      {/* Hero */}
      <section className="container-main text-center mb-20">
        <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6">
          Comment ça marche ?
        </span>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
          Un processus simple<br />
          <span className="text-gradient">et transparent</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          De la première idée à la mise en ligne, je vous accompagne à chaque étape. 
          Pas de jargon technique, pas de mauvaises surprises.
        </p>
      </section>

      {/* Steps */}
      <section className="container-main section-padding">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-border hidden md:block" />
          
          <div className="space-y-12 md:space-y-24">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={step.number}
                  className={`relative flex flex-col md:flex-row items-start gap-8 ${
                    isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background transform -translate-x-1/2 hidden md:block" />
                  
                  {/* Content */}
                  <div className={`flex-1 ${isEven ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <div className={`inline-flex items-center gap-3 mb-4 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                      <span className="text-5xl font-black text-primary/20">{step.number}</span>
                      <div className="p-3 rounded-xl bg-primary/10">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground mb-4">{step.description}</p>
                    <ul className={`space-y-2 ${isEven ? 'md:ml-auto' : ''}`}>
                      {step.details.map((detail, idx) => (
                        <li key={idx} className={`flex items-center gap-2 text-sm ${isEven ? 'md:justify-end' : ''}`}>
                          {!isEven && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                          <span>{detail}</span>
                          {isEven && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                        </li>
                      ))}
                    </ul>
                    <span className="inline-block mt-4 px-3 py-1 rounded-full bg-muted text-sm text-muted-foreground">
                      ⏱ {step.duration}
                    </span>
                  </div>
                  
                  {/* Spacer for alternating layout */}
                  <div className="flex-1 hidden md:block" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-main section-padding">
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Parlons de votre projet. Premier appel gratuit et sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tarifs#contact">
              <Button className="btn-primary text-lg px-8 py-6">
                Me contacter
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/tarifs">
              <Button variant="outline" className="text-lg px-8 py-6">
                Voir les tarifs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
